import { RootState } from "@store/store";
import { MenuItem } from "@dto/MenuItem";
import { Category } from "@dto/Category";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { selectClient } from "@store/client/clientReducer";
import { convertToCamel } from "@lib/convertToCamel";
import { getQueryStringValuesTyped, getQueryStringValueTyped } from "@lib/url";
import { coerceInteger } from "@lib/coerceInteger";

export interface MenuItemState {
  menuItemsWithPlan: MenuItem[];
  menuItem: MenuItem;
  menuItems: MenuItem[];
  categories: Category[];
  selectedCategoryIds: number[];
  selectedExclusiveCategoryIds: number[];
  planMenuItem: MenuItem | undefined;
  /**
   * The last menu item that was selected, specifically to return from User Plans'.
   */
  lastMenuItem: MenuItem;
  /**
   * The page number of the current entity list.
   */
  pageNumber: number;
}

const initialState = (): MenuItemState => {
  let allMenuItems = convertToCamel(VisitWidget.store.menuItems) as MenuItem[];

  // Exclude menu items, if present based on query string
  const excludedMenuItemIds = getQueryStringValuesTyped(
    "excluded_menu_item_ids",
    coerceInteger,
  ) as number[] | undefined;
  if ((excludedMenuItemIds ?? []).length > 0) {
    allMenuItems = allMenuItems.filter(
      (x) => !excludedMenuItemIds!.includes(x.id),
    );
  }

  // Exclude items currently unsupported in Widget. While we will add these over time, Group Plans
  // (SharedPlan) is unlikely to ever need to be displayed in widget (TODO: do not return SharedPlan
  // menu entity type or remove altogether, instead only Command Console.)
  allMenuItems = allMenuItems.filter(
    (x) =>
      x.entity !== "Goal" &&
      x.entity !== "SharedPlan" &&
      x.entity !== "SelfGuided" &&
      x.entity !== "Challenge",
  );

  // Include menu items, if present based on query string (requires backend logic, which at present
  // will only render the included menu items in `VisitWidget.store.menuItems`)
  const includedMenuItemIds = getQueryStringValuesTyped(
    "menu_item_ids",
    coerceInteger,
  ) as number[] | undefined;
  if ((includedMenuItemIds ?? []).length > 0) {
    allMenuItems = allMenuItems.filter((x) =>
      includedMenuItemIds!.includes(x.id),
    );
  }

  const menuItems = allMenuItems.filter((x) => x.entity !== "PlanItem");
  const planMenuItem = allMenuItems.find((x) => x.entity === "PlanItem");

  let menuItem = menuItems[0];
  const defaultMenuItemOverride = getQueryStringValueTyped(
    "menu_item_id",
    coerceInteger,
  );
  if (defaultMenuItemOverride) {
    // Allow query string to specify a different starting menu item.
    const menuItemOverride =
      menuItems.find((x) => x.id === defaultMenuItemOverride) ?? menuItems[0];
    if (menuItemOverride) {
      menuItem = menuItemOverride;
    }
  }

  const categories = convertToCamel(VisitWidget.store.categories) as Category[];
  const defaultSelectedCategoryIds = getQueryStringValuesTyped(
    "category_ids",
    coerceInteger,
  );
  let selectedCategoryIds = [] as number[];
  let selectedExclusiveCategoryIds = [] as number[];
  if (defaultSelectedCategoryIds?.length ?? 0 > 0) {
    selectedCategoryIds = categories
      .filter((x) => defaultSelectedCategoryIds!.includes(x.id) && !x.exclusive)
      .map((x) => x.id);
    selectedExclusiveCategoryIds = categories
      .filter((x) => defaultSelectedCategoryIds!.includes(x.id) && x.exclusive)
      .map((x) => x.id);
  }

  return {
    menuItemsWithPlan: allMenuItems,
    menuItems: menuItems,
    menuItem: menuItem,
    categories: categories,
    selectedCategoryIds: selectedCategoryIds,
    selectedExclusiveCategoryIds: selectedExclusiveCategoryIds,
    planMenuItem: planMenuItem,
    lastMenuItem: menuItem,
    pageNumber: 1,
  };
};

export const menuItemSlice = createSlice({
  name: "menuItem",
  initialState: initialState(),
  reducers: {
    setMenuItemAction: (
      state: MenuItemState,
      action: PayloadAction<MenuItem>,
    ) => {
      state.menuItem = action.payload;
      if (action.payload.entity !== "PlanItem") {
        state.pageNumber = 1;
      }
    },
    setLastMenuItemAction: (
      state: MenuItemState,
      action: PayloadAction<MenuItem>,
    ) => {
      state.lastMenuItem = action.payload;
    },
    setSelectedCategoryIdsAction: (
      state: MenuItemState,
      action: PayloadAction<number[]>,
    ) => {
      state.selectedCategoryIds = action.payload;
      state.pageNumber = 1;
    },
    setSelectedExclusiveCategoryIdsAction: (
      state: MenuItemState,
      action: PayloadAction<number[]>,
    ) => {
      state.selectedExclusiveCategoryIds = action.payload;
      state.pageNumber = 1;
    },
    setPageNumberAction: (
      state: MenuItemState,
      action: PayloadAction<number>,
    ) => {
      state.pageNumber = action.payload;
    },
  },
});

export const selectMenuItemsWithPlan = (state: RootState) => {
  return state.menuItem.menuItemsWithPlan;
};

export const selectMenuItems = (state: RootState) => {
  return state.menuItem.menuItems;
};

export const selectMenuItem = (state: RootState) => {
  return state.menuItem.menuItem;
};

/**
 * Select the last menu item that was not user Plan.
 */
export const selectLastMenuItem = (state: RootState) => {
  return state.menuItem.lastMenuItem;
};

export const selectPlanMenuItem = (state: RootState) => {
  return state.menuItem.planMenuItem;
};

export const selectExclusiveCategories = createSelector(
  [selectMenuItem, (state: RootState) => state.menuItem.categories],
  (menuItem, categories) => {
    return categories.filter((category) => {
      return (
        category.exclusive &&
        category.enabled &&
        menuItem.categoryIds.indexOf(category.id) > -1
      );
    });
  },
);

export const selectCategories = createSelector(
  [selectMenuItem, (state: RootState) => state.menuItem.categories],
  (menuItem, categories) => {
    return categories.filter((category) => {
      return (
        !category.exclusive &&
        category.enabled &&
        menuItem.categoryIds.indexOf(category.id) > -1
      );
    });
  },
);

/**
 * Select the explicitly selected category ids. By default no categories are selected.
 */
export const selectSelectedCategoryIds = (state: RootState) => {
  return state.menuItem.selectedCategoryIds;
};

/**
 * Select the explicitly selected exclusive category ids. By default no exclusive
 * categories are selected.
 */
export const selectSelectedExclusiveCategoryIds = (state: RootState) => {
  return state.menuItem.selectedExclusiveCategoryIds;
};

/**
 * Select non exclusive categories that apply to the current menu item.
 */
export const selectCategoryMenuCategories = createSelector(
  [
    selectClient,
    selectMenuItem,
    (state: RootState) => state.menuItem.categories,
  ],
  (client, menuItem, categories) => {
    return categories.filter((category) => {
      return (
        (!client.categoriesVisibleByMenuItem ||
          menuItem?.categoryIds.includes(category.id)) &&
        !category.exclusive
      );
    });
  },
);

export const selectSelectedCategories = createSelector(
  [selectCategoryMenuCategories, selectSelectedCategoryIds],
  (categories, selectedCategoryIds) => {
    const result = categories.filter((category) => {
      const selected =
        selectedCategoryIds.findIndex(
          (categoryId) => categoryId === category.id,
        ) > -1;

      // Categories with `onByDefault` turned off must be explicitly selected by the user.
      return (
        selected || (selectedCategoryIds.length === 0 && category.onByDefault)
      );
    });

    return result;
  },
);

/**
 * Select categories that apply to the current menu item.
 */
export const selectExclusiveCategoryMenuCategories = createSelector(
  [
    selectClient,
    selectMenuItem,
    (state: RootState) => state.menuItem.categories,
  ],
  (client, menuItem, categories) => {
    return categories.filter((category) => {
      return (
        (!client.categoriesVisibleByMenuItem ||
          menuItem?.categoryIds.includes(category.id)) &&
        category.exclusive
      );
    });
  },
);

/**
 * Select the exclusive categories that are selected. This is either all exclusive categories
 * for the selected menu item if the user has not explicitly selected any exclusive categories,
 * or the exclusive categories the user has explicitly selected.
 */
export const selectSelectedExclusiveCategories = createSelector(
  [selectExclusiveCategoryMenuCategories, selectSelectedExclusiveCategoryIds],
  (categories, selectedCategoryIds) => {
    const result = categories.filter((category) => {
      const selected =
        selectedCategoryIds.findIndex(
          (categoryId) => categoryId === category.id,
        ) > -1;

      // Categories with `onByDefault` turned off must be explicitly selected by the user.
      return (
        selected || (selectedCategoryIds.length === 0 && category.onByDefault)
      );
    });

    return result;
  },
);

/**
 * Select the page number the Entity List is at, used for map pins and entity list.
 */
export const selectPageNumber = (state: RootState) => {
  return state.menuItem.pageNumber;
};

export const {
  setMenuItemAction,
  setLastMenuItemAction,
  setSelectedCategoryIdsAction,
  setSelectedExclusiveCategoryIdsAction,
  setPageNumberAction,
} = menuItemSlice.actions;

// eslint-disable-next-line import/no-default-export
export default menuItemSlice.reducer;
